import { z } from 'zod';
import { ShadowSettingKeySchema } from '../setting/shadow-setting';
import { TransparencySettingSchema } from '../setting/transparency-setting';
import { BasicAnimationSetSchema } from './animations';
import { BasePinComponentSchema, BasePinComponentSettingsSchema, VectorSchema } from './base-pin-component';

/**
 * The portion of the full image that is visible within the bounds of the image
 * pin component.
 */
const CropAreaSchema = z.object({
  /**
   * The focus point of the crop area inside the full image, represented as
   * fractions between 0 and 1. Functions similarly to using percentages for the
   * `background-position` property in CSS.
   *
   * A value of (0, 0) means the focus is in the top-left corner, causing the
   * top-left corners of the image pin component and the full image to align,
   * while a value of (1, 1) means the bottom-right corners align.
   *
   * The default value is `(0.5, 0.5)`, which centers the full image in the
   * image pin component.
   */
  focus: VectorSchema,

  /**
   * The scale factor determines how much larger the full image is compared to
   * the image pin component, applied along the most constrained dimension
   * (either width or height).
   *
   * Note that there is currently no way to create distorted images (where the
   * aspect ratio no longer matches that of the original image), so we only need
   * a single scale value that is used for both dimensions.
   *
   * The default value is `1`, meaning the full image fits around the image pin
   * component with no room to spare. This behavior, combined with a focus of
   * `(0.5, 0.5)`, mimics the behavior of `background-size: cover`.
   */
  scale: z.number(),
});

export const ImagePinComponentPinThemeSettingsSchema = z.object({
  borderSize: z.number().optional(),
  borderColor: z.string().optional(),
  borderRadius: z.number().optional(),
  shadow: ShadowSettingKeySchema,
  transparency: TransparencySettingSchema,
});
export type ImagePinComponentPinThemeSettings = z.infer<typeof ImagePinComponentPinThemeSettingsSchema>;

const ImagePinComponentSettingsSchema = BasePinComponentSettingsSchema.extend(
  ImagePinComponentPinThemeSettingsSchema.extend(
    z.object({
      url: z.string(), // TODO: Can be empty in PoC, fix later .min(1),
      cropArea: CropAreaSchema.optional(),
      zoomableInInteractiveViewer: z.boolean().optional(),
      animations: BasicAnimationSetSchema.optional(),
    }).shape
  ).shape
);

export const ImagePinComponentTypeSchema = z.literal('IMAGE');

export const ImagePinComponentSchema = BasePinComponentSchema.extend(
  z.object({
    type: ImagePinComponentTypeSchema,
    settings: ImagePinComponentSettingsSchema,
  }).shape
);

/**
 * The portion of the full image that is visible within the bounds of the image
 * pin component.
 */
export type CropArea = z.infer<typeof CropAreaSchema>;

export type ImageSettings = z.infer<typeof ImagePinComponentSettingsSchema>;

export interface CropAreaExport {
  /**
   * The crop area that corresponds to "no cropping", i.e. focused on the centre
   * of the full image, and with the same size. This behaves similarly to
   * `background-size: cover` in CSS.
   */
  cover: CropArea;
}

export const CropArea: CropAreaExport = {
  cover: { focus: { x: 0.5, y: 0.5 }, scale: 1 },
};
